import { ArrowRight } from 'lucide-react'
import { Section, SectionHeading } from '../ui/Section'
import Reveal, { RevealGroup, RevealItem } from '../ui/Reveal'
import Counter from '../ui/Counter'
import KineticButton from '../ui/KineticButton'
import { scrollToSection } from '../../utils/helpers'
import { api } from '../../lib/api'
import { useApi } from '../../lib/useApi'
import { IMPACT } from '../../data/impact'

export default function Impact() {
  const data = useApi(api.impact, IMPACT) || IMPACT
  const stats = data.stats || []
  const programs = data.programs || []

  return (
    <Section id="impact" tone="raised">
      <SectionHeading eyebrow="Social impact" title={data.heading} lead={data.mission} />

      {/* Figures in a ruled row. */}
      {stats.length > 0 && (
        <RevealGroup each={0.1} className="surface mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-3xl !bg-outline-variant md:grid-cols-4">
          {stats.map((s, i) => (
            <RevealItem key={s.label} className="bg-sc-lowest px-6 py-8">
              <p className="font-mono text-4xl font-bold leading-none tracking-tight text-ink md:text-5xl">
                <Counter value={s.value} delay={0.1 * i} />
                <span className="text-primary">{s.suffix}</span>
              </p>
              <p className="mt-3 text-[13px] text-ink-3">{s.label}</p>
            </RevealItem>
          ))}
        </RevealGroup>
      )}

      {/* Programs: numbered columns. */}
      <RevealGroup each={0.1} className="mt-14 grid gap-x-10 gap-y-10 md:grid-cols-3 xl:gap-x-14">
        {programs.map((p, i) => (
          <RevealItem key={p.title}>
            <span className="font-mono text-xs font-semibold tracking-[0.12em] text-primary">{String(i + 1).padStart(2, '0')}</span>
            <h3 className="mt-3 text-2xl font-bold text-ink">{p.title}</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-ink-3">{p.description}</p>
          </RevealItem>
        ))}
      </RevealGroup>

      {/* Photos */}
      {data.images && data.images.length > 0 && (
        <RevealGroup each={0.06} className="mt-16 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {data.images.map((img, i) => (
            <RevealItem key={i} className="aspect-[4/3] overflow-hidden rounded-2xl shadow-[var(--elev-2)]">
              <img src={img} alt="" loading="lazy" className="h-full w-full object-cover" />
            </RevealItem>
          ))}
        </RevealGroup>
      )}

      {/* Call to action: a navy band across the section. */}
      {data.cta && (
        <Reveal className="band mt-16 flex flex-col gap-6 rounded-[2rem] p-8 md:flex-row md:items-center md:justify-between md:p-12">
          <div className="max-w-2xl">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] opacity-70">Partnerships</p>
            <h3 className="mt-2 text-3xl font-bold md:text-4xl">{data.cta.heading}</h3>
            <p className="mt-3 text-[15px] leading-relaxed opacity-80 md:text-base">{data.cta.description}</p>
          </div>
          <KineticButton onClick={() => scrollToSection('contact')} variant="filled" size="lg" className="shrink-0">
            Get involved
            <ArrowRight className="h-4 w-4" />
          </KineticButton>
        </Reveal>
      )}
    </Section>
  )
}
